import { Helmet } from "@dr.pogodin/react-helmet";
import { memo, useLayoutEffect, useMemo } from "react";
import { BASE_URL, devEnvironment, IS_LOCALHOST, REACT_LOGIN_PATH } from "@/config/Constants";
import "@/styles/index.scss";
import "@/styles/not-found.scss";
import { useTranslation } from "react-i18next";
import { useSessionContext } from "@/contexts/sessionContext";
import notFoundLottie from "@/assets/lottiefiles/json/404.json";
import { LogoOriginal } from "@/assets/icons/LogoOriginal";
import { useNavigate, useNavigationType, useRouteError } from "react-router";
import { URLAccessibleWithoutSession } from "@/config/RouteMap";
import Lottie from "lottie-react";

export default function NotFound() {
  const { t } = useTranslation();
  const { session: UserSession } = useSessionContext();

  let backToLink = "/";
  let backToLinkText = t("text_404_login_btn");

  if (UserSession?.status === "loggedin") {
    // backToLink = IS_LOCALHOST ? BASE_URL : "/kunden_beta/switchToBeta?targetPage=login";
    backToLinkText = t("text_404_dashboard_btn");
  }

  return (
    <>
      <Helmet>
        <title>
          {t("404_page_title")} - {t("jobspreader_board_text")}
        </title>
      </Helmet>
      <div className="error-404-page">
        <a href={backToLink}>
          <LogoOriginal />
        </a>
        <Lottie animationData={notFoundLottie} loop autoplay className="notfound-lottie" />
        <p dangerouslySetInnerHTML={{ __html: t(`v2_error_page_text_404`) }} />
        <a href={backToLink}>
          <button className="figma-btn fg-primary fg-large">
            <span>{backToLinkText}</span>
          </button>
        </a>
      </div>
    </>
  );
}

const ErrorCode = memo(() => {
  const { t } = useTranslation();
  const error = useRouteError();
  const { session: UserSession } = useSessionContext();

  const status = error?.status || 500;
  const data = useMemo(() => {
    try {
      return JSON.parse(error?.data);
    } catch (e) {
      return { error: e };
    }
  }, [error]);

  console.error("ErrorCode caught an error", error, data);

  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const userHasSession = data?.hasSession || Object.keys(UserSession || {}).length > 0;
  const backToLink = useMemo(() => {
    if (navigationType === "PUSH") {
      return "#";
    }

    if (userHasSession) {
      return BASE_URL;
    }

    return IS_LOCALHOST ? `${BASE_URL}/${URLAccessibleWithoutSession.login}` : REACT_LOGIN_PATH;
  }, [navigationType, userHasSession]);

  const handleButtonClick = (e) => {
    e.preventDefault();
    if (navigationType === "PUSH") {
      navigate(-1, { viewTransition: true });
      return;
    }

    if (userHasSession) {
      // navigate("/", { viewTransition: true });
      window.location.href = BASE_URL;
      return;
    }

    if (IS_LOCALHOST) {
      // navigate(URLAccessibleWithoutSession.login, { viewTransition: true });
      window.location.href = `${BASE_URL}/${URLAccessibleWithoutSession.login}`;
    } else {
      window.location.href = REACT_LOGIN_PATH;
    }
    return;
  };

  return (
    <>
      <Helmet>
        <title>
          {t(`v2_error_${status}_title`)} - {t("jobspreader_board_text")}
        </title>
      </Helmet>
      <div className="error-code-page">
        <LogoOriginal />
        <Lottie animationData={notFoundLottie} loop autoplay className="error-lottie" />
        <p dangerouslySetInnerHTML={{ __html: t(`v2_error_page_text_${status}`) }} />
        <a href={backToLink} onClick={handleButtonClick}>
          <button className="figma-btn fg-primary fg-large">
            <span>
              {navigationType === "POP"
                ? userHasSession
                  ? t("v2_error_page_button")
                  : t("text_404_login_btn")
                : t("v2_error_page_button_back")}
            </span>
          </button>
        </a>
      </div>
      {error?.message && (
        <div className="error-details">
          <p>{new Date().toLocaleString()}</p>
          <p>{error?.message}</p>
        </div>
      )}
    </>
  );
});

/**
 * this component catches errors thrown in the route components
 * and displays an error page if status is present in the error object,
 * otherwise it displays the error name, message, stack trace and code
 */
export function ErrorFallbackComponent() {
  const error = useRouteError();

  useLayoutEffect(() => {
    if (
      error?.name === "TypeError" &&
      error?.message?.includes("Failed to fetch dynamically imported") &&
      !window.location.hostname.includes("localhost")
    ) {
      window.location.reload();
    }
    // eslint-disable-next-line
  }, []);

  console.error("ErrorFallbackComponent caught an error", error);

  if (error?.status || !devEnvironment) {
    return <ErrorCode />;
  }

  return (
    <div className="error-code-page">
      <LogoOriginal />
      <p>{error?.name}</p>
      <div style={{ color: "#dd5050" }}>{error?.message}</div>
      <details>
        <summary>Stack Trace (expand for screenshot)</summary>
        <pre>{error?.stack}</pre>
      </details>
      {error?.code && <p>Error Code: {error?.code}</p>}
      <button className="figma-btn fg-primary fg-large" onClick={() => window.location.reload()}>
        <span>Retry</span>
      </button>
    </div>
  );
}
